/**
 * The NAME a copy takes (refonte modulaire §4.2, grill of 2026-09-21; issue
 * #115): « X (copie) », then « X (copie 2) », « X (copie 3) », against the names
 * already taken.
 *
 * One calculation, two dresses, because the lot needs it twice:
 *
 * - the name of a duplicated ENTITY - a criterion, a persona, a protocol, an
 *   experience - and the name of the three ITEMS that carry one, « les seuls
 *   items qui portent un nom - l'étape, l'objectif, la page » (§4.2). Six call
 *   sites, bounded at 120, 80, 120, 80, 80, 80 and 60;
 * - the name of a duplicated VARIABLE, which counts in plain numbers (« email »
 *   -> « email 2 ») and is bounded at 40 (§8.6).
 *
 * An item may legitimately have NO name - a draft schema relaxes the `min(1)`
 * a version enforces, because the autosave fires while somebody types it - so
 * both variants leave a blank source blank. A copy must not be born carrying a
 * name nobody wrote, here « (copie) », and §4.2 says as much of the gesture:
 * the copy is selected with « son champ de nom avec elle quand elle en a un ».
 *
 * ## Why the marker is stripped FIRST
 *
 * Duplicating « Ancrage (copie 2) » has to give « Ancrage (copie 3) » and never
 * « Ancrage (copie 2) (copie) ». So the source's own trailing ` (copie)` or
 * ` (copie N)` comes off before anything is appended, and the search starts
 * again from the base. One marker is removed and not all of them: « X (copie)
 * (copie) » is a name somebody typed by hand, and quietly rewriting it into
 * « X (copie 2) » would rename a thing the operator never asked to rename.
 *
 * The strip is case-SENSITIVE while the taken-check below is not, and that is
 * deliberate rather than sloppy: the marker is the one this function writes, so
 * it is always spelled « copie » in lower case. « (COPIE) » in a name is a word
 * the operator chose, not a marker to eat.
 *
 * ## Why it scans upward from the first candidate
 *
 * §4.2 spells it for the variable variant - « plus petit numéro libre dans la
 * timeline » - and the entity variant reads the same way (« X (copie) », PUIS
 * « X (copie 2) »). So: the first candidate nothing holds, scanned from the
 * bottom. A slot freed by a deletion is reused rather than stepped over, which
 * is what somebody who just deleted « Ancrage (copie) » expects to get back,
 * and the alternative - always continuing above the highest number in use -
 * never reuses anything and drifts to « (copie 47) » on a list of three.
 *
 * It has one consequence the spec accepts and a reader should not mistake for a
 * defect: a copy can be numbered BELOW its source. Duplicating « etape 3 » in a
 * timeline holding « etape 1 » and « etape 3 » gives « etape 2 », which is the
 * smallest free number and therefore the right answer by §4.2. A variable name
 * is a LABEL and not an order (§8.6), so the number ranks nothing.
 *
 * ## Why the base is trimmed and never the suffix
 *
 * The bound belongs to the STORE, and a name over it is a 400 the operator
 * cannot act on. Trimming the suffix instead would be worse than useless: it is
 * what tells the copy apart, and two names cut to the same string are a
 * uniqueness refusal on top of the length one. So the base yields, down to
 * nothing if it must.
 *
 * The one thing a caller owes this function: `maxLength` at least as long as
 * the suffix - « (copie 2) » is ten characters, and the tightest bound in the
 * repo is 40. Below that the result overflows the bound rather than cutting the
 * counter, deliberately: two candidates cut to the same string would be a
 * search that never ends, which is worse than a name the save refuses with a
 * message about its length.
 *
 * ## Why the two variants compare taken names differently
 *
 * They mirror what actually refuses them, and the two refusals do not agree:
 *
 * - an entity name is refused on `name_key`, which the store writes with
 *   `normalizedName` below, so « ancrage (copie) » would be refused against
 *   « Ancrage (copie) ». This function therefore folds through that very
 *   function - #115 carried a copy of it, because that lot did not touch the
 *   backend, and #116 brought the original down here rather than let two folds
 *   drift apart;
 * - a variable name is refused by `experienceProblems`, which compares raw
 *   strings, so « Email » and « email » are two names there. Folding would make
 *   this function skip a candidate the save would have accepted.
 */

/** A trailing ` (copie)` or ` (copie N)`, the marker this module writes. */
const COPY_MARK = / \(copie(?: \d+)?\)$/;

/** A trailing ` N`, the marker the numeric variant writes. */
const NUMBER_MARK = / \d+$/;

/**
 * The normalized name: what the unique index of §4 is built on, and what this
 * module compares a copy's candidate against.
 *
 * « Insensible à la casse et aux espaces de bord » (§4), and nothing more -
 * internal spacing is kept, because « Écoute active » and « Écoute  active »
 * are two names an operator can tell apart on a screen.
 *
 * ONE function, and it is the reason `name_key` is a stored column rather than
 * a `lower(trim(name))` index: SQLite's built-in `lower()` folds ASCII and
 * nothing else, so the base would let « CRITÈRE » sit beside « critère » while
 * this function calls them the same. The rule is spelled here and the base
 * stores its result, so the two cannot disagree - not even on an accent.
 *
 * It lives in `shared` and not in the backend, and #116 is what moved it: the
 * name a copy takes is computed HERE against names the backend will refuse
 * THERE, so the two sides have to fold identically. The day one of them learned
 * to fold inner spaces or accents and the other did not, a suffix this module
 * called free would be refused by the index - and the refusal would land on a
 * name no operator ever typed.
 *
 * `toLowerCase` and NOT `toLocaleLowerCase`: the first is locale-independent by
 * specification, the second reads the host's locale. A key that folded
 * differently depending on the container's locale would make the same two names
 * collide on one deploy and not on the next.
 */
export function normalizedName(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * The base, plus the suffix, cut to the bound - by shortening the BASE only.
 *
 * `trimEnd` after the cut so a base severed mid-space does not leave « Nom
 *  (copie) » with two spaces in it.
 */
function candidateOf(base: string, suffix: string, maxLength: number): string {
  const room = maxLength - suffix.length;
  if (room <= 0) return suffix.trimStart();
  const kept = base.length <= room ? base : cutToUnits(base, room).trimEnd();
  return kept === '' ? suffix.trimStart() : `${kept}${suffix}`;
}

/**
 * `base` cut to `units` UTF-16 code units, landing on a code point boundary.
 *
 * The count has to be in code UNITS, because that is what the bound counts:
 * Zod's `.max()` reads `String.length`, and so does the column behind it. But
 * `slice` alone can land BETWEEN the two halves of a surrogate pair, and a lone
 * surrogate is not a character any store refuses or any screen draws - the copy
 * is simply born with a « ? » where its name ends. Dropping the orphan costs one
 * comparison and removes the class.
 *
 * A combining mark severed from its base letter is NOT handled, and the two are
 * not the same defect: that string is well formed, it just reads one accent
 * short. Chasing it would mean segmenting graphemes to place a cut whose only
 * job is to fit a length bound.
 *
 * Exported since #116, for the ONE other place in the repo that cuts a string
 * to a stored bound: the automatic note of a duplicated v1
 * (`backend/src/versioning/store.ts`), which shortens the source name to make
 * room for its parenthetical. Two spellings of « cut to N units » would be two
 * places to learn about surrogate pairs, and only one of them would.
 */
export function cutToUnits(base: string, units: number): string {
  const cut = base.slice(0, units);
  const last = cut.charCodeAt(cut.length - 1);
  return last >= 0xd800 && last <= 0xdbff ? cut.slice(0, -1) : cut;
}

/**
 * The first candidate nothing holds, scanning upward from `from`.
 *
 * It terminates, and the reason is worth writing down: two different `n` give
 * two different suffixes, the suffix is never cut, so the candidates are
 * pairwise distinct - and `taken` is finite, so the scan cannot outrun it by
 * more than one step.
 */
function firstFreeName(
  base: string,
  from: number,
  suffixOf: (n: number) => string,
  maxLength: number,
  taken: Iterable<string>,
  fold: (name: string) => string,
): string {
  const seen = new Set([...taken].map(fold));
  let n = from;
  let candidate = candidateOf(base, suffixOf(n), maxLength);
  while (seen.has(fold(candidate))) {
    n += 1;
    candidate = candidateOf(base, suffixOf(n), maxLength);
  }
  return candidate;
}

/** « (copie) » for the first one, « (copie N) » after it - never « (copie 1) ». */
function copySuffix(n: number): string {
  return n === 1 ? ' (copie)' : ` (copie ${n})`;
}

/**
 * The name a duplicated ENTITY takes: « X (copie) », « X (copie 2) », ...
 *
 * The bare base is NEVER a candidate: the scan starts at « (copie) », so a copy
 * is always marked as one whatever `taken` holds. What `taken` decides is only
 * which marker is free. For an ENTITY it is the names the store would refuse
 * against - the NON-RETIRED ones of the same kind (§4); for an ITEM, where names
 * are not unique and homonyms are tolerated (§4.2, §7.2), it is the siblings the
 * palette `@` would show side by side, which is the whole reason the suffix
 * exists there.
 *
 * `maxLength` is the bound of whatever is being named: `CRITERION_NAME_MAX`
 * (120), `PERSONA_NAME_MAX` (80), `PROTOCOL_NAME_MAX` (120),
 * `EXPERIENCE_NAME_MAX` (80), `PROTOCOL_STEP_NAME_MAX` (80),
 * `PROTOCOL_OBJECTIVE_NAME_MAX` (80), `EXPERIENCE_PAGE_NAME_MAX` (60). It is
 * passed rather than chosen here so this stays a calculation and never a table
 * of who bounds what.
 */
export function nextFreeCopyName(
  source: string,
  taken: Iterable<string>,
  maxLength: number,
): string {
  if (source.trim() === '') return '';
  // The strip runs on the source and not on the blank check above, so a name
  // that is ONLY a marker - « (copie) », typed by hand - still gets one rather
  // than being emptied into a name the version schema refuses.
  const base = source.replace(COPY_MARK, '').trim();
  return firstFreeName(base, 1, copySuffix, maxLength, taken, normalizedName);
}

/**
 * The name a duplicated VARIABLE takes: « email » -> « email 2 » (§8.6).
 *
 * An EMPTY name stays empty, and that is the rule and not an edge case: a block
 * whose variable name has not been typed declares nothing (`namedVariablesOf`
 * skips it), so a duplicate of it must not gain a name the operator never
 * wrote. `taken` is what `namedVariablesOf` renders for the whole timeline -
 * the name is unique across the timeline, not within a page - and duplicating a
 * page renames block after block, so a caller doing that adds each result to
 * the list before asking for the next.
 *
 * The count starts at 2 because « email 1 » is not a name anybody writes; a
 * source that already ends in a number has it stripped, so « email 2 » gives
 * « email 3 » when 2 is taken, exactly as the copy marker behaves.
 */
export function nextFreeVariableName(
  source: string,
  taken: Iterable<string>,
  maxLength: number,
): string {
  const trimmed = source.trim();
  if (trimmed === '') return '';
  const base = trimmed.replace(NUMBER_MARK, '').trim();
  return firstFreeName(
    base,
    2,
    (n) => ` ${n}`,
    maxLength,
    taken,
    (name) => name,
  );
}
